import type {
  OverviewExportRecord,
  OverviewExportStatus,
} from "../Apis/overviewExportTypes";

export type ExportPollEntity = "patients" | "tasks" | "opportunities";

/** Backend retries a FAILED export once after ~30s. Keep polling through that window. */
export const EXPORT_FAILED_GRACE_MS = 120_000;

/** CAR-1051 / CAR-2738: poll every 5s, max ~10 minutes for a stuck QUEUED/PROCESSING job. */
export const EXPORT_POLL_INTERVAL_MS = 5000;
export const EXPORT_POLL_MAX_ATTEMPTS = 120;
export const EXPORT_POLL_MAX_MS =
  EXPORT_POLL_INTERVAL_MS * EXPORT_POLL_MAX_ATTEMPTS;

export function isExportPollTimedOut(
  startedAtMs: number | undefined,
  nowMs: number = Date.now(),
  maxMs: number = EXPORT_POLL_MAX_MS
): boolean {
  if (typeof startedAtMs !== "number" || !Number.isFinite(startedAtMs)) {
    return false;
  }
  return nowMs - startedAtMs >= maxMs;
}

export interface PollExportStatusOptions {
  exportId: string;
  getStatus: (
    exportId: string
  ) => Promise<{ success: boolean; export?: OverviewExportRecord }>;
  intervalMs?: number;
  maxAttempts?: number;
  /** How long to keep polling after the first FAILED before treating it as final. */
  failedGraceMs?: number;
  onProgress?: (status: OverviewExportStatus, processedCount?: number) => void;
}

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => {
    setTimeout(resolve, ms);
  });
}

/**
 * Poll export status until COMPLETED, terminal FAILED, or timeout.
 * Per CAR-1051: poll every 3–5 seconds, max ~10 minutes.
 * Per CAR-2738: a FAILED status can be temporary while BullMQ retries.
 */
export async function pollExportStatus(
  options: PollExportStatusOptions
): Promise<OverviewExportRecord> {
  const {
    exportId,
    getStatus,
    intervalMs = EXPORT_POLL_INTERVAL_MS,
    maxAttempts = EXPORT_POLL_MAX_ATTEMPTS,
    failedGraceMs = EXPORT_FAILED_GRACE_MS,
    onProgress,
  } = options;

  let attempts = 0;
  let failedSince: number | null = null;

  while (attempts < maxAttempts) {
    if (attempts > 0) {
      await sleep(intervalMs);
    }

    const data = await getStatus(exportId);
    const record = data.export;

    if (!record) {
      throw new Error("Export not found.");
    }

    if (record.status === "COMPLETED") {
      onProgress?.("COMPLETED", record.processedCount);
      return record;
    }

    if (record.status === "FAILED") {
      if (failedSince == null) failedSince = Date.now();
      if (Date.now() - failedSince >= failedGraceMs) {
        throw new Error(record.error || "Export failed");
      }
      attempts += 1;
      continue;
    }

    failedSince = null;
    onProgress?.(record.status, record.processedCount);
    attempts += 1;
  }

  throw new Error(
    "Export timed out. Please check export history or try again."
  );
}
